import { getFilterGroupType } from 'module/helpers/filter/grouped-field-transformer';
import { FilterableField } from 'module/types/filter-field/field/filterable-field';
import { GroupedOptions } from 'module/types/filter-field/field/grouped-field';
import {
  AiFieldFilter,
  AiFieldFilterType,
} from 'module/types/filter-field/filter';
import { GroupCriteria } from 'module/types/filter-field/grouped-filter/filter';
import { AiRichTextEditor } from 'module/ui/components/atoms/ai/rich-text-editor';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Short01 } from 'module/ui/components/styles';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { Button } from '@rocketlane/ui';
import { Form as AntForm } from 'antd';
import { FC, useCallback, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { criterion } from './criterion';
import { getDefaultCriteria } from './get-default-criteria';
import { GroupedFieldSelect } from './grouped-field-select';
import { BaseFilterComponentProps } from '../types';

export const RemoveButton = styled(Button).attrs({
  variant: 'ghost',
  type: 'button',
  size: 'small',
  leftIcon: <FontAwesomeIcon icon={faXmark} />,
})`
  ${FontAwesomeIcon} {
    color: var(--icon-secondary);
  }

  ${({ onClick }) =>
    onClick != null
      ? css`
          visibility: visible;
        `
      : css`
          visibility: hidden;
        `};
`;
const Label = styled.div`
  min-width: 40px;
  ${Short01};
  opacity: 0.5;
  text-align: right;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  height: 32px;
  min-height: 32px;
  padding: 4px 0;
`;

const Form = styled(AntForm)`
  .ant-form-item {
    margin-right: var(--spacing-03);
  }
  .ant-form-item:last-child {
    margin-right: 0;
  }

  .ant-form-item-control-input {
    height: 100%;
  }
`;

interface Props extends BaseFilterComponentProps {
  hideRemoveButton?: boolean;
  hideMatchTypeText?: boolean;
  matchTypeLabel: string | React.ReactNode;
  fields: GroupedOptions<FilterableField>[];
  filter?: GroupCriteria;
  onChange: (filter: GroupCriteria) => void;
  onRemove?: () => void;
}

const StyledForm = styled(Form)`
  width: 100%;
  display: inline-flex;
  align-items: flex-start;
  flex-wrap: nowrap;
`;

const StyledFormItem = styled(Form.Item)`
  flex: 1;
  margin-bottom: 0 !important;
  display: flex;
  align-items: flex-start;

  .ant-form-item-control {
    flex: 1;
    width: 100%;
    min-width: 0;
  }

  .ant-row {
    flex: 1;
  }

  .ant-form-item-control-input {
    flex: 1;
    width: 100%;
    min-width: 0;
  }

  .ant-form-item-control-input-content {
    width: 100%;
    display: flex;
    min-width: 0;
  }
`;

export const GroupedCriteria: FC<Props> = ({
  hideRemoveButton = false,
  hideMatchTypeText = false,
  fields,
  matchTypeLabel,
  filter,
  onChange,
  onRemove,
  getFilterOperationTypeOptions,
  getSelectEditorComponents,
  getOperationOptionRenderer,
  userOptionsGetter,
}) => {
  const onFieldChange = useCallback(
    (selectedField: FilterableField) => {
      onChange({
        id: getFilterGroupType(selectedField.sourceType),
        criteria: getDefaultCriteria(selectedField),
      });
    },
    [onChange],
  );

  const isInitialized = useRef(false);

  useEffect(() => {
    isInitialized.current = true;
  }, []);

  return (
    <StyledForm layout="inline">
      {hideRemoveButton ? null : (
        <Form.Item>
          <RemoveButton onClick={onRemove} />
        </Form.Item>
      )}
      {hideMatchTypeText ? null : (
        <Form.Item>
          <Label>{matchTypeLabel}</Label>
        </Form.Item>
      )}
      {filter?.criteria?.type !== AiFieldFilterType.Ai ? (
        <>
          <Form.Item>
            <GroupedFieldSelect
              fields={fields}
              onChange={onFieldChange}
              title={matchTypeLabel}
              value={filter != null ? filter.criteria.field : filter}
            />
          </Form.Item>
          {criterion({
            filter: filter?.criteria,
            onChange: (fieldFilter) =>
              onChange({
                id: getFilterGroupType(fieldFilter.field.sourceType),
                criteria: fieldFilter,
              }),
            getFilterOperationTypeOptions,
            getSelectEditorComponents,
            getOperationOptionRenderer,
            userOptionsGetter,
          })}
        </>
      ) : (
        <>
          <StyledFormItem>
            <AiRichTextEditor
              value={{
                content: (filter?.criteria as any)?.value || '',
                editedByUser: !(filter?.criteria as AiFieldFilter)
                  ?.isAiGenerated,
              }}
              onChange={(newValue) => {
                if (isInitialized.current) {
                  onChange({
                    ...filter!,
                    criteria: {
                      ...filter!.criteria,
                      value: newValue.content,
                      isAiGenerated: false,
                    } as AiFieldFilter,
                  });
                }
              }}
            />
          </StyledFormItem>
        </>
      )}
    </StyledForm>
  );
};
